import { CAT_FOOD_PRICE, CROP_IDS, CROPS } from "../data/crops";
import { CAT_IDS, CAT_RARITY_LABEL, CATS } from "../data/cats";
import { DECORATION_IDS, DECORATIONS } from "../data/decorations";
import type { GameEventBus } from "../events";
import { getCropStage, getGrowthProgress, getRemainingMs } from "../systems/CropSystem";
import type { CatId, CropId, DecorationType, GameState } from "../types";
import type { GameStore } from "../state/GameStore";

type PanelMode = "none" | "plot" | "cat" | "inventory" | "decor" | "decoration";

export class GameUI {
  private root!: HTMLDivElement;
  private selectedPlotId: string | null = null;
  private selectedCatId: CatId | null = null;
  private selectedDecorationId: string | null = null;
  private mode: PanelMode = "none";
  private shopTab: "buy" | "sell" | "cats" = "buy";
  private creatorDraft: {
    bodyType: "soft" | "bright";
    hairStyle: "bob" | "ponytail";
    hairColor: string;
    outfit: "lavender" | "peach";
    outfitColor: string;
  } = {
    bodyType: "soft",
    hairStyle: "bob",
    hairColor: "cocoa",
    outfit: "lavender",
    outfitColor: "lavender",
  };
  private toastTimer?: number;
  private timerInterval?: number;

  constructor(
    private readonly store: GameStore,
    private readonly events: GameEventBus,
  ) {}

  mount(root: HTMLDivElement): void {
    this.root = root;
    this.root.innerHTML = [
      '<div class="top-hud">',
      '  <div class="brand-pill"><span class="asset-icon ui-cat brand-paw"></span><div><strong>Cat Town</strong><small>ฟาร์มของคุณ</small></div></div>',
      '  <div class="level-pill" aria-label="เลเวลและค่าประสบการณ์"><span id="level-label">เลเวล 1</span><span class="exp-track"><span id="exp-fill"></span></span></div>',
      '  <div class="coin-pill" aria-label="จำนวนเหรียญ"><span class="asset-icon ui-coin"></span><strong id="coin-value">0</strong><button type="button" class="coin-add" data-tool="shop" aria-label="เปิดร้านค้า"></button></div>',
      '</div>',
      '<div class="top-utilities" aria-label="เมนูเพิ่มเติม">',
      '  <button type="button" data-action="settings" aria-label="ตั้งค่า"><span class="asset-icon ui-settings"></span></button>',
      '  <button type="button" data-action="inbox" aria-label="กล่องข้อความ"><span class="asset-icon ui-mail"></span></button>',
      '</div>',
      '<nav class="tool-rail" aria-label="เมนูเกม">',
      '  <button type="button" class="icon-only" data-tool="inventory" aria-label="เมล็ดพันธุ์และกระเป๋า"><span class="asset-icon ui-plant"></span></button>',
      '  <button type="button" data-tool="cats"><span class="asset-icon ui-cat"></span><span>แมว</span></button>',
      '  <button type="button" data-tool="shop"><span class="asset-icon ui-shop"></span><span>ร้านค้า</span></button>',
      '  <button type="button" class="icon-only" data-tool="decor" aria-label="แต่งฟาร์ม"><span class="asset-icon ui-decor"></span></button>',
      '</nav>',
      '<button type="button" id="cat-hud" class="cat-hud" data-tool="cats" aria-label="ดูสถานะแมว" hidden>',
      '  <span class="cat-hud-avatar cat-khram"></span>',
      '  <span class="cat-hud-meters">',
      '    <span class="cat-hud-row"><span class="asset-icon ui-food"></span><span class="heart-strip" data-cat-meter="hunger"><i></i><i></i><i></i><i></i></span></span>',
      '    <span class="cat-hud-row happy"><span class="asset-icon ui-happy"></span><span class="heart-strip" data-cat-meter="happiness"><i></i><i></i><i></i><i></i></span></span>',
      '  </span>',
      '</button>',
      '<section id="context-panel" class="context-panel" aria-live="polite" hidden></section>',
      '<div id="toast" class="toast" role="status" aria-live="polite" hidden></div>',
      '<div id="placement-banner" class="placement-banner" hidden><span>เลือกช่องว่างในฟาร์ม</span><button type="button" data-action="cancel-placement"><span class="asset-icon ui-close mini"></span> ยกเลิก</button></div>',
      '<div id="shop-modal" class="modal-backdrop" hidden>',
      '  <section class="shop-modal" role="dialog" aria-modal="true" aria-labelledby="shop-title">',
      '    <header><div><small>Moonflower Market & Adoption</small><h2 id="shop-title">ตลาดและศูนย์รับเลี้ยง</h2></div><button type="button" class="icon-button" data-action="close-modal" aria-label="ปิด"><span class="asset-icon ui-close"></span></button></header>',
      '    <div class="shop-tabs" role="tablist"><button type="button" data-shop-tab="buy" class="active">ซื้อของ</button><button type="button" data-shop-tab="sell">ขายผลผลิต</button><button type="button" data-shop-tab="cats">รับเลี้ยงแมว</button></div>',
      '    <div id="shop-content" class="shop-content"></div>',
      '  </section>',
      '</div>',
      '<div id="character-modal" class="modal-backdrop character-backdrop" hidden>',
      '  <section class="character-modal" role="dialog" aria-modal="true" aria-labelledby="creator-title">',
      '    <div class="creator-preview"><span class="creator-aura"></span><span id="creator-character" class="creator-character"></span><small>Animation-ready farmer</small></div>',
      '    <div class="creator-controls"><small>ยินดีต้อนรับสู่ Cat Town</small><h2 id="creator-title">สร้างชาวสวนของคุณ</h2><p>เริ่มจากฟาร์มเล็ก ๆ หาเงิน แล้วรับเลี้ยงแมวตัวแรกด้วยตัวเอง</p>',
      '      <label class="creator-name">ชื่อเล่น<input id="player-name" maxlength="18" value="ชาวสวน"></label>',
      '      <div class="creator-group"><strong>รูปร่าง</strong><div><button type="button" data-creator-body="soft" class="active">ละมุน</button><button type="button" data-creator-body="bright">กระฉับกระเฉง</button></div></div>',
      '      <div class="creator-group"><strong>ทรงผม</strong><div><button type="button" data-creator-hair="bob" class="active">บ๊อบ</button><button type="button" data-creator-hair="ponytail">มัดผม</button></div></div>',
      '      <div class="creator-group"><strong>สีผม</strong><div><button type="button" data-creator-hair-color="cocoa" class="color-choice cocoa active" aria-label="น้ำตาลโกโก้"></button><button type="button" data-creator-hair-color="honey" class="color-choice honey" aria-label="น้ำผึ้ง"></button></div></div>',
      '      <div class="creator-group"><strong>ชุดทำฟาร์ม</strong><div><button type="button" data-creator-outfit="lavender" class="active">ลาเวนเดอร์</button><button type="button" data-creator-outfit="peach">พีช</button></div></div>',
      '      <button type="button" class="primary-button creator-start" data-action="create-player">เริ่มชีวิตในฟาร์ม</button>',
      '    </div>',
      '  </section>',
      '</div>',
    ].join("");

    this.root.addEventListener("click", (event) => this.handleClick(event));
    this.root.querySelectorAll<HTMLButtonElement>("[data-shop-tab]").forEach((button) => {
      button.addEventListener("click", (event) => {
        event.stopPropagation();
        const tab = button.dataset.shopTab;
        if (tab === "buy" || tab === "sell" || tab === "cats") {
          this.shopTab = tab;
          this.renderShop();
        }
      });
    });
    this.store.subscribe(() => this.render());
    this.events.on("plot:selected", ({ plotId }) => {
      this.dismissWelcome();
      this.selectedPlotId = plotId;
      this.selectedCatId = null;
      this.selectedDecorationId = null;
      this.mode = "plot";
      this.renderPanel();
    });
    this.events.on("cat:selected", ({ catId }) => {
      this.dismissWelcome();
      this.selectedCatId = catId as CatId;
      this.selectedPlotId = null;
      this.selectedDecorationId = null;
      this.mode = "cat";
      this.renderPanel();
    });
    this.events.on("decoration:selected", ({ decorationId }) => {
      this.dismissWelcome();
      this.selectedDecorationId = decorationId;
      this.selectedPlotId = null;
      this.selectedCatId = null;
      this.mode = "decoration";
      this.renderPanel();
    });
    this.events.on("toast", ({ message, tone }) => this.toast(message, tone));
    this.events.on("scene:ready", () => {
      const loading = document.querySelector<HTMLDivElement>("#loading-screen");
      loading?.remove();
    });

    this.timerInterval = window.setInterval(() => {
      if (this.mode === "plot") this.renderPanel();
      this.store.tick();
    }, 1_000);

    const player = this.store.getSnapshot().player;
    this.creatorDraft = {
      bodyType: player.bodyType,
      hairStyle: player.hairStyle,
      hairColor: player.hairColor,
      outfit: player.outfit,
      outfitColor: player.outfitColor,
    };
    const characterModal = this.root.querySelector<HTMLDivElement>("#character-modal");
    if (characterModal) characterModal.hidden = player.created;
    this.renderCreatorPreview();
    this.render();
  }

  private handleClick(event: MouseEvent): void {
    const target = (event.target as HTMLElement).closest<HTMLElement>("button,[data-action]");
    if (!target) return;
    if (target.dataset.creatorBody) {
      this.creatorDraft.bodyType = target.dataset.creatorBody as "soft" | "bright";
      this.updateCreatorChoices("creatorBody", target.dataset.creatorBody);
      return;
    }
    if (target.dataset.creatorHair) {
      this.creatorDraft.hairStyle = target.dataset.creatorHair as "bob" | "ponytail";
      this.updateCreatorChoices("creatorHair", target.dataset.creatorHair);
      return;
    }
    if (target.dataset.creatorHairColor) {
      this.creatorDraft.hairColor = target.dataset.creatorHairColor;
      this.updateCreatorChoices("creatorHairColor", target.dataset.creatorHairColor);
      this.renderCreatorPreview();
      return;
    }
    if (target.dataset.creatorOutfit) {
      this.creatorDraft.outfit = target.dataset.creatorOutfit as "lavender" | "peach";
      this.creatorDraft.outfitColor = target.dataset.creatorOutfit;
      this.updateCreatorChoices("creatorOutfit", target.dataset.creatorOutfit);
      this.renderCreatorPreview();
      return;
    }
    this.dismissWelcome();

    const tool = target.dataset.tool;
    if (tool === "shop") this.openShop();
    if (tool === "inventory") {
      this.mode = "inventory";
      this.renderPanel();
    }
    if (tool === "cats") {
      this.mode = "cat";
      this.selectedCatId = null;
      this.renderPanel();
    }
    if (tool === "decor") {
      this.mode = "decor";
      this.renderPanel();
    }

    const action = target.dataset.action;
    if (action === "create-player") {
      const name = this.root.querySelector<HTMLInputElement>("#player-name")?.value ?? "ชาวสวน";
      this.guard(() => this.store.createPlayer({ displayName: name, ...this.creatorDraft }), "พร้อมแล้ว เริ่มทำฟาร์มกัน!");
      const modal = this.root.querySelector<HTMLDivElement>("#character-modal");
      if (modal) modal.hidden = true;
      this.events.emit("player:created", {});
      return;
    }
    if (action === "close-modal") this.closeShop();
    if (action === "close-welcome") this.root.querySelector("#welcome-card")?.remove();
    if (action === "settings") this.toast("ตั้งค่าพร้อมใช้งาน — เกมบันทึกอัตโนมัติ", "success");
    if (action === "inbox") this.toast("ยังไม่มีข้อความใหม่", "success");
    if (action === "close-panel") {
      this.mode = "none";
      this.renderPanel();
    }
    if (action === "cancel-placement") {
      this.events.emit("decoration:place-cancel", {});
      this.setPlacementBanner(false);
    }
    if (action === "water" && this.selectedPlotId) {
      const success = this.guard(() => this.store.water(this.selectedPlotId!), "รดน้ำแล้ว ผักกำลังเติบโต");
      if (success) this.events.emit("player:action", { action: "water" });
      this.events.emit("plot:updated", { plotId: this.selectedPlotId });
    }
    if (action === "harvest" && this.selectedPlotId) {
      const success = this.guard(() => this.store.harvest(this.selectedPlotId!), "เก็บเกี่ยวสำเร็จ!");
      if (success) this.events.emit("player:action", { action: "harvest" });
      this.events.emit("plot:updated", { plotId: this.selectedPlotId });
    }
    if (action === "plant" && this.selectedPlotId && target.dataset.crop) {
      const cropId = target.dataset.crop as CropId;
      const success = this.guard(() => this.store.plant(this.selectedPlotId!, cropId), "ปลูก " + CROPS[cropId].name + " แล้ว");
      if (success) this.events.emit("player:action", { action: "plant" });
      this.events.emit("plot:updated", { plotId: this.selectedPlotId });
    }
    if (action === "feed-cat" && this.selectedCatId) {
      const success = this.guard(() => this.store.feedCat(this.selectedCatId!), "แมวอิ่มและมีความสุขขึ้น");
      if (success) this.events.emit("cat:fed", { catId: this.selectedCatId });
    }
    if (action === "pet-cat" && this.selectedCatId) {
      const success = this.guard(() => this.store.petCat(this.selectedCatId!), "ลูบหัวแล้ว แมวมีความสุขขึ้น!");
      if (success) this.events.emit("cat:petted", { catId: this.selectedCatId });
    }
    if (action === "select-cat" && target.dataset.cat) {
      this.selectedCatId = target.dataset.cat as CatId;
      this.renderPanel();
    }
    if (action === "buy-seed" && target.dataset.crop) {
      const cropId = target.dataset.crop as CropId;
      this.guard(() => this.store.buySeed(cropId), "ซื้อเมล็ด " + CROPS[cropId].name + " แล้ว");
      this.renderShop();
    }
    if (action === "sell-produce" && target.dataset.crop) {
      const cropId = target.dataset.crop as CropId;
      this.guard(() => this.store.sellProduce(cropId), "ขาย " + CROPS[cropId].name + " แล้ว");
      this.renderShop();
    }
    if (action === "buy-food") {
      this.guard(() => this.store.buyCatFood(), "ซื้ออาหารแมวแล้ว");
      this.renderShop();
    }
    if (action === "open-cat-shop") {
      this.shopTab = "cats";
      this.openShop();
    }
    if (action === "buy-cat" && target.dataset.cat) {
      const catId = target.dataset.cat as CatId;
      const success = this.guard(() => this.store.buyCat(catId), `ยินดีต้อนรับ ${CATS[catId].name} สู่ฟาร์ม!`);
      if (success) {
        this.events.emit("cat:owned", { catId });
        this.selectedCatId = catId;
      }
      this.renderShop();
    }
    if (action === "buy-decoration" && target.dataset.decoration) {
      const type = target.dataset.decoration as DecorationType;
      this.guard(() => this.store.buyDecoration(type), "ซื้อ " + DECORATIONS[type].name + " แล้ว");
      this.renderShop();
    }
    if (action === "place-decoration" && target.dataset.decoration) {
      const type = target.dataset.decoration as DecorationType;
      this.events.emit("decoration:place-start", { type });
      this.setPlacementBanner(true);
      this.mode = "none";
      this.renderPanel();
    }
    if (action === "move-decoration" && this.selectedDecorationId) {
      const decoration = this.store.getSnapshot().decorations.find((entry) => entry.id === this.selectedDecorationId);
      if (decoration) {
        this.events.emit("decoration:place-start", {
          type: decoration.type,
          existingId: decoration.id,
        });
        this.setPlacementBanner(true);
        this.mode = "none";
        this.renderPanel();
      }
    }
    if (action === "store-decoration" && this.selectedDecorationId) {
      const id = this.selectedDecorationId;
      this.guard(() => this.store.storeDecoration(id), "เก็บของตกแต่งเข้าคลังแล้ว");
      this.events.emit("decoration:updated", {});
      this.mode = "decor";
      this.selectedDecorationId = null;
      this.renderPanel();
    }

    const tab = target.dataset.shopTab;
    if (tab === "buy" || tab === "sell" || tab === "cats") {
      this.shopTab = tab;
      this.renderShop();
    }
  }

  private render(): void {
    const state = this.store.getSnapshot();
    const needed = state.level * 30;
    this.setText("#coin-value", state.coins.toLocaleString("th-TH"));
    this.setText("#level-label", "เลเวล " + state.level);
    const fill = this.root.querySelector<HTMLElement>("#exp-fill");
    if (fill) fill.style.width = Math.min(100, state.exp / needed * 100) + "%";
    this.renderCatHud(state);
    this.renderPanel();
    if (!this.root.querySelector<HTMLDivElement>("#shop-modal")?.hidden) this.renderShop();
  }

  private renderPanel(): void {
    const panel = this.root.querySelector<HTMLElement>("#context-panel");
    if (!panel) return;
    const state = this.store.getSnapshot();
    const ownedCats = state.cats.filter((cat) => cat.owned);
    const catHud = this.root.querySelector<HTMLButtonElement>("#cat-hud");
    if (catHud) catHud.hidden = this.mode !== "none" || ownedCats.length === 0;
    if (this.mode === "none") {
      panel.hidden = true;
      return;
    }

    panel.hidden = false;
    if (this.mode === "plot" && this.selectedPlotId) {
      const plot = state.plots.find((entry) => entry.id === this.selectedPlotId);
      if (!plot) return;
      const stage = getCropStage(plot);
      const header = this.panelHeader("แปลงผัก", "เลือกการดูแลแปลงนี้");
      if (stage === "empty") {
        const seeds = CROP_IDS.map((cropId) => {
          const crop = CROPS[cropId];
          return '<button type="button" class="choice-card" data-action="plant" data-crop="' + cropId + '"' +
            (state.inventory.seeds[cropId] <= 0 ? " disabled" : "") + ">" +
            this.cropIcon(cropId) + '<span><strong>' + crop.name + '</strong><small>เมล็ด ' +
            state.inventory.seeds[cropId] + "</small></span></button>";
        }).join("");
        panel.innerHTML = header + '<div class="choice-grid">' + seeds + "</div>";
      } else if (stage === "dry") {
        panel.innerHTML = header + '<div class="status-row">' + this.cropIcon(plot.cropId!) + '<div><strong>' +
          CROPS[plot.cropId!].name + '</strong><small>เพิ่งปลูก ต้องการน้ำ</small></div><button type="button" class="primary-button" data-action="water"><span class="asset-icon ui-water mini"></span> รดน้ำ</button></div>';
      } else if (stage === "ready") {
        panel.innerHTML = header + '<div class="status-row">' + this.cropIcon(plot.cropId!, "ready") + '<div><strong>' +
          CROPS[plot.cropId!].name + '</strong><small>พร้อมเก็บเกี่ยวแล้ว</small></div><button type="button" class="primary-button harvest" data-action="harvest"><span class="asset-icon ui-bag mini"></span> เก็บเกี่ยว</button></div>';
      } else {
        const progress = Math.round(getGrowthProgress(plot) * 100);
        const seconds = Math.ceil(getRemainingMs(plot) / 1000);
        panel.innerHTML = header + '<div class="growing-card"><div class="growing-copy">' +
          this.cropIcon(plot.cropId!) + '<div><strong>' + CROPS[plot.cropId!].name +
          '</strong><small>โตอีกประมาณ ' + seconds + " วินาที</small></div></div><div class=\"growth-track\"><span style=\"width:" +
          progress + '%\"></span></div></div>';
      }
      return;
    }

    if (this.mode === "cat") {
      if (!this.selectedCatId) {
        if (ownedCats.length === 0) {
          panel.innerHTML = this.panelHeader("ยังไม่มีแมวในฟาร์ม", "ปลูก เก็บเกี่ยว และขายผลผลิตเพื่อเก็บเงิน") +
            '<div class="empty-cat-state"><span class="asset-icon ui-cat"></span><h3>แมวตัวแรกคือความสำเร็จของคุณ</h3><p>เมื่อมี 720 เหรียญ สามารถรับเลี้ยงมะลิได้ที่ศูนย์รับเลี้ยง</p><button type="button" class="primary-button" data-action="open-cat-shop"><span class="asset-icon ui-shop mini"></span> ไปศูนย์รับเลี้ยง</button></div>';
          return;
        }
        const cards = ownedCats.map((cat) => '<button type="button" class="cat-list-card" data-action="select-cat" data-cat="' +
          cat.id + '">' + this.catPortrait(cat.id) + '<span><strong>' +
          cat.name + "</strong><small>" + cat.breed + '</small></span><span class="card-arrow">›</span></button>').join("");
        panel.innerHTML = this.panelHeader("แมวในฟาร์ม", "แตะแมวในฉากหรือเลือกจากรายการ") + '<div class="cat-list">' + cards + "</div>";
        return;
      }
      const cat = state.cats.find((entry) => entry.id === this.selectedCatId);
      if (!cat?.owned) return;
      panel.innerHTML = this.panelHeader(cat.name, cat.breed) +
        '<div class="cat-profile">' + this.catPortrait(cat.id, "large") + '<div class="cat-status"><div class="meter-line"><span><span class="asset-icon ui-food tiny"></span> ความอิ่ม</span><div><b style="width:' +
        cat.hunger + '%"></b></div><strong>' + Math.round(cat.hunger) + '%</strong></div><div class="meter-line happy"><span><span class="status-heart">♥</span> ความสุข</span><div><b style="width:' +
        cat.happiness + '%"></b></div><strong>' + Math.round(cat.happiness) + '%</strong></div><div class="panel-actions"><button type="button" class="primary-button" data-action="feed-cat"' +
        (state.inventory.catFood <= 0 ? " disabled" : "") + '><span class="asset-icon ui-food mini"></span> ให้อาหาร (' + state.inventory.catFood +
        ')</button><button type="button" class="secondary-button" data-action="pet-cat"><span class="asset-icon ui-cat mini"></span> ลูบหัว</button></div></div></div>';
      return;
    }

    if (this.mode === "inventory") {
      const seedRows = CROP_IDS.map((id) => '<span class="inventory-item">' + this.cropIcon(id, "tiny") + CROPS[id].name + " <strong>" + state.inventory.seeds[id] + "</strong></span>").join("");
      const produceRows = CROP_IDS.map((id) => '<span class="inventory-item">' + this.cropIcon(id, "tiny") + CROPS[id].name + " <strong>" + state.inventory.produce[id] + "</strong></span>").join("");
      panel.innerHTML = this.panelHeader("กระเป๋าฟาร์ม", "สิ่งของทั้งหมดบันทึกอัตโนมัติ") +
        '<div class="inventory-columns"><div><h3>เมล็ด</h3>' + seedRows + '</div><div><h3>ผลผลิต</h3>' + produceRows +
        '</div><div><h3>อาหารแมว</h3><span class="inventory-item"><span class="asset-icon ui-food tiny"></span>ถ้วยอาหาร <strong>' + state.inventory.catFood + "</strong></span></div></div>";
      return;
    }

    if (this.mode === "decor") {
      const cards = DECORATION_IDS.map((type) => '<button type="button" class="choice-card" data-action="place-decoration" data-decoration="' +
        type + '"' + (state.inventory.decorations[type] <= 0 ? " disabled" : "") + '>' + this.decorThumb(type) + '<span><strong>' + DECORATIONS[type].name + '</strong><small>มี ' +
        state.inventory.decorations[type] + "</small></span></button>").join("");
      panel.innerHTML = this.panelHeader("แต่งฟาร์ม", "เลือกของแล้วแตะช่องว่างในฟาร์ม") + '<div class="choice-grid">' + cards + "</div>";
      return;
    }

    if (this.mode === "decoration" && this.selectedDecorationId) {
      const decoration = state.decorations.find((entry) => entry.id === this.selectedDecorationId);
      if (!decoration) return;
      panel.innerHTML = this.panelHeader(DECORATIONS[decoration.type].name, "ของตกแต่งในฟาร์ม") +
        '<div class="panel-actions"><button type="button" class="primary-button" data-action="move-decoration"><span class="asset-icon ui-decor mini"></span> ย้าย</button><button type="button" class="secondary-button" data-action="store-decoration"><span class="asset-icon ui-bag mini"></span> เก็บเข้าคลัง</button></div>';
    }
  }

  private panelHeader(title: string, subtitle: string): string {
    return '<header class="panel-header"><div><h2>' + title + '</h2><p>' + subtitle + '</p></div><button type="button" class="icon-button" data-action="close-panel" aria-label="ปิด"><span class="asset-icon ui-close"></span></button></header>';
  }

  private openShop(): void {
    const modal = this.root.querySelector<HTMLDivElement>("#shop-modal");
    if (!modal) return;
    modal.hidden = false;
    this.renderShop();
  }

  private closeShop(): void {
    const modal = this.root.querySelector<HTMLDivElement>("#shop-modal");
    if (modal) modal.hidden = true;
  }

  private renderShop(): void {
    const content = this.root.querySelector<HTMLDivElement>("#shop-content");
    if (!content) return;
    const state = this.store.getSnapshot();
    this.root.querySelectorAll<HTMLButtonElement>("[data-shop-tab]").forEach((button) => {
      button.classList.toggle("active", button.dataset.shopTab === this.shopTab);
    });
    if (this.shopTab === "buy") {
      const seedRows = CROP_IDS.map((id) => {
        const crop = CROPS[id];
        return '<article class="shop-row">' + this.cropIcon(id, "shop-art") + '<div><strong>เมล็ด' + crop.name +
          "</strong><small>มี " + state.inventory.seeds[id] + '</small></div><button type="button" data-action="buy-seed" data-crop="' +
          id + '" ' + (state.coins < crop.seedPrice ? "disabled" : "") + '><span class="asset-icon ui-coin tiny"></span> ' + crop.seedPrice + "</button></article>";
      }).join("");
      const food = '<article class="shop-row"><span class="asset-icon ui-food shop-art"></span><div><strong>อาหารแมว</strong><small>มี ' +
        state.inventory.catFood + '</small></div><button type="button" data-action="buy-food" ' +
        (state.coins < CAT_FOOD_PRICE ? "disabled" : "") + '><span class="asset-icon ui-coin tiny"></span> ' + CAT_FOOD_PRICE + "</button></article>";
      const decor = DECORATION_IDS.map((type) => '<article class="shop-row">' + this.decorThumb(type, "shop-art") + '<div><strong>' + DECORATIONS[type].name + "</strong><small>มี " +
        state.inventory.decorations[type] + '</small></div><button type="button" data-action="buy-decoration" data-decoration="' +
        type + '" ' + (state.coins < DECORATIONS[type].price ? "disabled" : "") + '><span class="asset-icon ui-coin tiny"></span> ' +
        DECORATIONS[type].price + "</button></article>").join("");
      content.innerHTML = '<h3>เมล็ดและอาหาร</h3>' + seedRows + food + '<h3>ของตกแต่ง</h3>' + decor;
    } else if (this.shopTab === "sell") {
      const rows = CROP_IDS.map((id) => {
        const crop = CROPS[id];
        return '<article class="shop-row">' + this.cropIcon(id, "shop-art") + '<div><strong>' + crop.name +
          "</strong><small>มี " + state.inventory.produce[id] + '</small></div><button type="button" data-action="sell-produce" data-crop="' +
          id + '" ' + (state.inventory.produce[id] <= 0 ? "disabled" : "") + '>ขาย <span class="asset-icon ui-coin tiny"></span> ' +
          crop.sellPrice + "</button></article>";
      }).join("");
      content.innerHTML = '<h3>ผลผลิตพร้อมขาย</h3>' + rows;
    } else {
      const cards = CAT_IDS.map((id) => {
        const definition = CATS[id];
        const stateCat = state.cats.find((cat) => cat.id === id)!;
        const missing = Math.max(0, definition.price - state.coins);
        const buttonLabel = stateCat.owned
          ? "อยู่ในครอบครัวแล้ว"
          : missing > 0
            ? `ขาดอีก ${missing.toLocaleString("th-TH")}`
            : "รับเลี้ยง";
        return '<article class="adoption-card rarity-' + definition.rarity + '">' +
          '<div class="adoption-portrait">' + this.catPortrait(id, "adoption-art") + '<span>' + CAT_RARITY_LABEL[definition.rarity] + '</span></div>' +
          '<div class="adoption-copy"><h3>' + definition.name + '</h3><strong>' + definition.breed + '</strong><p>' + definition.personality + '</p><small>' + definition.furColor + ' · ' + definition.appearance + '</small></div>' +
          '<div class="adoption-price"><span><span class="asset-icon ui-coin tiny"></span>' + definition.price.toLocaleString("th-TH") + '</span><button type="button" data-action="buy-cat" data-cat="' + id + '" ' +
          (stateCat.owned || missing > 0 ? "disabled" : "") + '>' + buttonLabel + '</button></div></article>';
      }).join("");
      content.innerHTML = '<div class="adoption-intro"><span class="asset-icon ui-cat"></span><div><h3>ศูนย์รับเลี้ยง Moonflower</h3><p>แมวทุกตัวรอเจ้าของที่พร้อมดูแล หาเหรียญจากผลผลิตแล้วพากลับบ้านได้เลย</p></div></div><div class="adoption-grid">' + cards + '</div>';
    }
  }

  private setPlacementBanner(visible: boolean): void {
    const banner = this.root.querySelector<HTMLDivElement>("#placement-banner");
    if (banner) banner.hidden = !visible;
  }

  private guard(action: () => void, success: string): boolean {
    try {
      action();
      this.toast(success, "success");
      return true;
    } catch (error) {
      this.toast(error instanceof Error ? error.message : "ทำรายการไม่สำเร็จ", "warning");
      return false;
    }
  }

  private toast(message: string, tone: "success" | "warning" | "info" = "info"): void {
    const toast = this.root.querySelector<HTMLDivElement>("#toast");
    if (!toast) return;
    toast.textContent = message;
    toast.dataset.tone = tone;
    toast.hidden = false;
    if (this.toastTimer) window.clearTimeout(this.toastTimer);
    this.toastTimer = window.setTimeout(() => {
      toast.hidden = true;
    }, 2_200);
  }

  private setText(selector: string, value: string): void {
    const element = this.root.querySelector<HTMLElement>(selector);
    if (element) element.textContent = value;
  }

  private dismissWelcome(): void {
    this.root.querySelector("#welcome-card")?.remove();
  }

  private renderCatHud(state: GameState): void {
    const catHud = this.root.querySelector<HTMLButtonElement>("#cat-hud");
    const cat = state.cats.find((entry) => entry.owned);
    if (!cat) {
      if (catHud) catHud.hidden = true;
      return;
    }
    if (catHud && this.mode === "none") catHud.hidden = false;
    const avatar = this.root.querySelector<HTMLElement>(".cat-hud-avatar");
    if (avatar) {
      avatar.className = `cat-hud-avatar cat-${cat.id}`;
      catHud?.setAttribute("aria-label", `ดูสถานะของ${cat.name}`);
    }
    for (const [meter, value] of [["hunger", cat.hunger], ["happiness", cat.happiness]] as const) {
      const filled = Math.max(0, Math.min(4, Math.ceil(value / 25)));
      this.root.querySelectorAll<HTMLElement>(`[data-cat-meter="${meter}"] i`).forEach((heart, index) => {
        heart.classList.toggle("is-filled", index < filled);
      });
    }
  }

  private updateCreatorChoices(datasetKey: "creatorBody" | "creatorHair" | "creatorHairColor" | "creatorOutfit", value: string): void {
    const attribute = datasetKey.replace(/[A-Z]/g, (letter) => `-${letter.toLowerCase()}`);
    this.root.querySelectorAll<HTMLElement>(`[data-${attribute}]`).forEach((button) => {
      button.classList.toggle("active", button.dataset[datasetKey] === value);
    });
    this.renderCreatorPreview();
  }

  private renderCreatorPreview(): void {
    const preview = this.root.querySelector<HTMLElement>("#creator-character");
    if (!preview) return;
    preview.dataset.hair = this.creatorDraft.hairStyle;
    preview.dataset.hairColor = this.creatorDraft.hairColor;
    preview.dataset.outfit = this.creatorDraft.outfit;
    preview.dataset.body = this.creatorDraft.bodyType;
  }

  private cropIcon(id: CropId, extraClass = ""): string {
    return '<span class="asset-icon crop-art ui-' + id + ' ' + extraClass + '"></span>';
  }

  private catPortrait(id: CatId, extraClass = ""): string {
    return '<span class="cat-avatar cat-' + id + ' ' + extraClass + '"></span>';
  }

  private decorThumb(type: DecorationType, extraClass = ""): string {
    return '<span class="decor-art decor-' + type + ' ' + extraClass + '"></span>';
  }
}
